/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */
import * as React from 'react';
import {
  Avatar, AvatarGroup, Box, Divider, Typography,
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import {
  chain, sortBy,
} from 'lodash';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import DOMPurify from 'isomorphic-dompurify';
import { useDispatch, useSelector } from 'react-redux';
import socket from 'utils/socket';

import { ExpertHubLayout } from 'components/templates';
import { selectAuthUser } from 'base/auth/selectors';
import { selectSchedule, selectScheduleStateLoader } from 'pages/schedules/selector';
import { ISchedule, IScheduleGroupByDate } from 'pages/schedules/interface';
import { schedulesActions } from 'pages/schedules/slice';
import { dashboardActions } from 'pages/dashboard/slice';
import {
  selectCoachingHours, selectExpertEarning, selectExpertEngagement, selectLoadingExpertEarning, selectSessionsOverview, selectTopSessions,
} from 'pages/dashboard/selector';
import { selectEnterprise } from 'base/core/selectors';
import {
  ExpertEarningTimeRange,
  AnalyticsTypes,
  IWebsocketResponse,
} from 'pages/dashboard/interface';

import { removeTags, truncateString, getUserName } from 'utils/common-utils';
import { getTotalFromArray } from 'utils/data-utils';
import { secondsToHoursAndMinutes, getCurrencySymbol, amountFormat } from 'utils/formats';

import {
  Loader, PrimaryButton, PrimarySelect, WhiteBgCard,
} from '../../../../components';
import { SecondaryBadge } from '../../../../components/molecules/custom-badges';
import {
  ArrowUpIcon, EyeIcon, HeartIcon, MessageIcon,
} from '../../../../assets/icons';
import './layout.scss';
import CountdownTimer from '../count-down-timer';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const avatarStyle = {
  width: 32,
  height: 32,
};

const progressBgStyle = {
  height: 33,
  borderRadius: 1.5,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '14px',
  paddingRight: '14px',
  marginBottom: '16px',
};

const coachingProgressStyle = {
  height: 7,
  display: 'flex',
};

const sessionBoxStyle = {
  borderRadius: 1.5,
  px: {
    xs: 2,
  },
  py: {
    xs: 1.25,
    sm: 3,
    md: 3,
    lg: 3,
  },
};

export const greyBoxStyle = {
  border: 1,
  borderColor: 'divider',
  backgroundColor: 'background.default',
  borderRadius: 1.5,
  padding: 2,
};

interface ChartLabelsProps {
  currency: string;
  sessionsAmount: number;
  profileAmount: number;
}

const ChartLabels: React.FC<ChartLabelsProps> = (
  { currency, sessionsAmount, profileAmount },
) => (
  <div className="row mx-0 gx-3">
    <div className="col-auto d-flex align-items-center mb-2 mb-sm-0">
      <Box
        sx={{
          mr: 1,
          width: 6,
          height: 6,
          backgroundColor: '#9092BE',
          borderRadius: 5,
        }}
      />

      <Typography variant="body2" sx={{ mr: 1 }}>
        Sessions
      </Typography>
      <Typography variant="h6">
        {`${getCurrencySymbol(currency)} ${amountFormat(sessionsAmount)}`}
      </Typography>
    </div>

    <div className="col-auto d-flex align-items-center mb-2 mb-sm-0">
      <Box
        sx={{
          mr: 1,
          width: 6,
          height: 6,
          backgroundColor: '#F7C85C',
          borderRadius: 5,
        }}
      />

      <Typography variant="body2" sx={{ mr: 1 }}>
        Profile
      </Typography>
      <Typography variant="h6">
        {`${getCurrencySymbol(currency)} ${amountFormat(profileAmount)}`}
      </Typography>
    </div>

  </div>
);

const DashboardView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authUser = useSelector(selectAuthUser);
  const currencyType = authUser?.attributes.currency;
  const { id: entId } = useSelector(selectEnterprise);
  const schedulesData: ISchedule[] = useSelector(selectSchedule);
  const loading = useSelector(selectScheduleStateLoader);
  const earningsLoading = useSelector(selectLoadingExpertEarning);
  const topSessions = useSelector(selectTopSessions);
  const coachingHours = useSelector(selectCoachingHours);
  const expertEngagement = useSelector(selectExpertEngagement);
  const sessionsOverview = useSelector(selectSessionsOverview);
  const {
    pendingProfileEarnings, pendingSessionEarnings,
    sessionEarnings, profileEarnings,
  } = useSelector(selectExpertEarning);

  const [activeSchedule, setActiveSchedule] = React.useState<ISchedule | undefined>(undefined);
  const [dateTimeAfterThreeDays, setDateTimeAfterThreeDays] = React.useState<number>(24 * 60 * 60 * 1000);
  const sortByOptions = [
    { id: 0, value: ExpertEarningTimeRange.MONTHLY, text: 'This month' },
    { id: 1, value: ExpertEarningTimeRange.YEARLY, text: 'This year' },
  ];
  const [selectedSortBy, setSelectedSortBy] = React.useState<string>(sortByOptions[0].value);
  const [isConnected, setIsConnected] = React.useState(socket.connected);

  const totalDuration = coachingHours.reduce((sum, item) => sum + item.totalDuration, 0);
  const totalLiveClassDuration = coachingHours.filter((item) => item.sessionType === 'LIVE class').reduce((sum, item) => sum + item.totalDuration, 0);
  const totalLiveSessionDuration = coachingHours.filter((item) => item.sessionType === 'LIVE session').reduce((sum, item) => sum + item.totalDuration, 0);
  const handleSortByChange = (event: any) => { setSelectedSortBy(event.target.value); };

  const totalPendingSessionEarning = React.useMemo(() => getTotalFromArray(pendingSessionEarnings), [pendingSessionEarnings]);
  const totalPendingProfileEarning = React.useMemo(() => getTotalFromArray(pendingProfileEarnings), [pendingProfileEarnings]);
  const totalPendingEarnings = totalPendingSessionEarning + totalPendingProfileEarning;

  React.useEffect(() => {
    const grouped = chain(schedulesData)
      .sortBy((item: ISchedule) => item.from)
      .groupBy('date')
      .map((value, key) => ({ date: key, events: value }))
      .value();
    const groupSorted: IScheduleGroupByDate[] = sortBy(grouped, (item: IScheduleGroupByDate) => item.date);

    let foundNextSession = false;
    groupSorted.forEach((group) => {
      if (!foundNextSession) {
        group.events.forEach((event: ISchedule) => {
          if (!foundNextSession) {
            if (moment().isSameOrBefore(event.meta.slot.from) && !event?.isCancel) {
              setActiveSchedule(event);
              setDateTimeAfterThreeDays(moment(event.meta.slot.from).toDate().getTime());
              foundNextSession = true;
            }
          }
        });
      }
    });
  }, [schedulesData]);

  React.useEffect(() => {
    const dateFrom = moment().format('YYYY-MM-DD');
    const dateTo = moment().subtract(0, 'months').endOf('month').format('YYYY-MM-DD');

    const payload = {
      from: dateFrom,
      to: dateTo,
    };

    dispatch(schedulesActions.getScheduleList(payload));
  }, [dispatch]);

  const openNextSession = () => {
    if (activeSchedule && activeSchedule !== undefined) {
      navigate(`/sessions/summary/${activeSchedule.id}`);
    }
  };

  const sendMessage = () => {
    const startDate = moment().startOf(selectedSortBy === ExpertEarningTimeRange.MONTHLY ? 'month' : 'year').format('YYYY-MM-DD');
    const endDate = moment().endOf(selectedSortBy === ExpertEarningTimeRange.MONTHLY ? 'month' : 'year').format('YYYY-MM-DD');
    dispatch(dashboardActions.getCoachingHours());
    dispatch(dashboardActions.getExpertEarning());
    dispatch(dashboardActions.getExpertEngagement());
    dispatch(dashboardActions.getSessionsOverview());
    dispatch(dashboardActions.getTopSessions());

    socket.emit('bulk', {
      type: AnalyticsTypes.EXPERT_COACHING_HOURS,
      startDate,
      endDate,
      entId,
      platformId: 'expert-hub',
    });
    socket.emit('bulk', {
      type: AnalyticsTypes.EXPERT_EARNING,
      startDate,
      endDate,
      entId,
      currency: currencyType,
      timeRange: selectedSortBy,
      platformId: 'expert-hub',
    });
    socket.emit('bulk', {
      type: AnalyticsTypes.EXPERT_ENGAGEMENT,
      startDate,
      endDate,
      entId,
      platformId: 'expert-hub',
    });
    socket.emit('bulk', {
      type: AnalyticsTypes.EXPERT_SESSIONS_OVERVIEW,
      startDate,
      endDate,
      entId,
      platformId: 'expert-hub',
    });
    socket.emit('bulk', {
      type: AnalyticsTypes.EXPERT_TOP_SESSIONS,
      startDate,
      endDate,
      entId,
      platformId: 'expert-hub',
    });
  };

  const connect = () => {
    if (!isConnected) {
      socket.connect();
    } else {
      socket.disconnect();
      socket.connect();
    }
  };

  React.useEffect(() => {
    if (entId) {
      connect();
    }
  }, [entId, currencyType]);

  React.useEffect(() => {
    function onConnect() {
      setIsConnected(true);
      socket.emit('join', { entId, platformId: 'expert-hub' });
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    function onResponseEvent(value: IWebsocketResponse) {
      if (value.connected) {
        sendMessage();
      }
      if (value.type === AnalyticsTypes.EXPERT_COACHING_HOURS) {
        if (value.response.success) {
          dispatch(dashboardActions.getCoachingHoursSucceeded(value.response.durations));
        } else {
          dispatch(dashboardActions.getCoachingHoursFailed());
        }
      }
      if (value.type === AnalyticsTypes.EXPERT_EARNING) {
        if (value.response.success) {
          console.log({ value });
          dispatch(dashboardActions.getExpertEarningSucceeded(value.response));
        } else {
          dispatch(dashboardActions.getExpertEarningFailed());
        }
      }
      if (value.type === AnalyticsTypes.EXPERT_ENGAGEMENT) {
        if (value.response.success) {
          dispatch(dashboardActions.getExpertEngagementSucceeded(value.response));
        } else {
          dispatch(dashboardActions.getExpertEngagementFailed());
        }
      }
      if (value.type === AnalyticsTypes.EXPERT_SESSIONS_OVERVIEW) {
        if (value.response.success) {
          dispatch(dashboardActions.getSessionsOverviewSucceeded(value.response));
        } else {
          dispatch(dashboardActions.getSessionsOverviewFailed());
        }
      }
      if (value.type === AnalyticsTypes.EXPERT_TOP_SESSIONS) {
        if (value.response.success) {
          dispatch(dashboardActions.getTopSessionsSucceeded(value.response.sessions));
        } else {
          dispatch(dashboardActions.getTopSessionsFailed());
        }
      }
    }
    function onErrorEvent(value: any) {
      console.log(value);
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('response', onResponseEvent);
    socket.on('error', onErrorEvent);

    return () => {
      if (isConnected) {
        socket.disconnect();
      }
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('response', onResponseEvent);
      socket.on('error', onErrorEvent);
    };
  }, [entId, currencyType]);

  React.useEffect(() => {
    sendMessage();
  }, [selectedSortBy]);

  const data = {
    labels: selectedSortBy === ExpertEarningTimeRange.MONTHLY
      ? Array.from({ length: moment().daysInMonth() }, (x, i) => moment().startOf('month').add(i, 'days').format('DD')) : moment.monthsShort(),
    datasets: [{
      label: 'Session',
      backgroundColor: '#9092BE',
      data: pendingSessionEarnings,
    }, {
      label: 'Profile',
      backgroundColor: '#FFD36D',
      data: pendingProfileEarnings,
    }],
  };

  const options: ChartOptions = {
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  return (
    <ExpertHubLayout>
      <div className="container px-0">
        <Box sx={{ mb: 4 }} className="row">
          <div className="col-md-6 mb-4 mb-md-0">
            <Typography variant="h4" sx={{ color: 'text.primary', mb: 0.5 }}>
              {`Welcome, ${getUserName(authUser)},`}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Overview of your wallet and income.
            </Typography>
          </div>
          <div className="col-md-6 text-md-end d-none d-md-block">
            <PrimarySelect
              options={sortByOptions}
              className="w-auto"
              inlineLabel
              label="Duration"
              onChange={handleSortByChange}
              value={selectedSortBy}
            />
          </div>
          <div className="col-md-6 text-md-end d-block d-md-none">
            <PrimarySelect
              options={sortByOptions}
              className="w-100"
              inlineLabel
              label="Sort"
              onChange={handleSortByChange}
              value={selectedSortBy}
            />
          </div>
          <br />
        </Box>

        <Loader loading={loading}>
          <div className="row">
            <div className="col-12 col-lg-7 mb-4">
              <WhiteBgCard>
                <Loader loading={earningsLoading}>
                  <div className="row mb-4 align-items-start">
                    <div className="col-12 col-md-auto">
                      <div className="d-flex flex-column">
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Total Earnings
                        </Typography>
                        <div className="d-flex align-items-center">
                          <Typography variant="h4" sx={{ color: 'text.primary', pr: 2 }}>
                            {`${getCurrencySymbol(currencyType)} ${amountFormat(totalPendingEarnings)}`}
                          </Typography>

                          {/* below box is hidden for now */}
                          <Box
                            sx={{
                              backgroundColor: 'secondary.light', py: 1, px: 1.75, borderRadius: 1.5, visibility: 'hidden',
                            }}
                            className="d-flex align-items-center"
                          >
                            <ArrowUpIcon sx={{
                              color: 'secondary.dark', fontSize: 16, mr: 1,
                            }}
                            />

                            <Typography sx={{ color: 'secondary.dark' }} variant="body2">
                              $0
                            </Typography>
                          </Box>
                        </div>
                      </div>

                    </div>
                    <div className="col d-none d-md-flex justify-content-md-end">

                      <ChartLabels
                        currency={currencyType}
                        sessionsAmount={totalPendingSessionEarning}
                        profileAmount={totalPendingProfileEarning}
                      />

                    </div>
                  </div>
                  <Bar data={data} options={options} />

                  <div className="mt-4 d-flex flex-wrap d-md-none">
                    <ChartLabels
                      currency={currencyType}
                      sessionsAmount={totalPendingSessionEarning}
                      profileAmount={totalPendingProfileEarning}
                    />
                  </div>
                </Loader>
              </WhiteBgCard>

            </div>
            <div className="col-12 col-lg-5 mb-4">
              <WhiteBgCard>
                <Typography variant="body2" sx={{ mb: 2 }}>Upcoming session</Typography>
                {activeSchedule && !activeSchedule.isCancel ? (
                  <>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {activeSchedule?.title}
                    </Typography>
                    <Box className="mb-4 htmlDescription" sx={{ color: 'grey.A700', mb: 2 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activeSchedule?.description && activeSchedule.description.length > 0 ? activeSchedule.description : '') }} />
                    <Typography variant="body2" sx={{ mb: 2.5 }}>
                      {`${moment(activeSchedule?.date, 'YYYY-MM-DD').format('Do MMMM YYYY')}, ${activeSchedule?.from} - ${activeSchedule?.to}`}
                    </Typography>
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <SecondaryBadge>{activeSchedule?.sessionType}</SecondaryBadge>
                      </div>
                      <div className="col">
                        <Box className="d-flex align-items-center">
                          <AvatarGroup max={3}>
                            {activeSchedule?.purchaseSummary?.latestBookedUsers.map((user: any) => (
                              <Avatar
                                key={user?.id}
                                sx={[avatarStyle]}
                                alt={user?.name}
                                src={user?.avatar}
                              >
                                {user?.name.charAt(0)}
                              </Avatar>
                            ))}
                          </AvatarGroup>
                          <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                            {activeSchedule?.purchaseSummary?.totalBookedCount > 0
                              ? `${activeSchedule?.purchaseSummary?.totalBookedCount} users have booked this session` : 'Still, no one booked this session'}
                          </Typography>
                        </Box>
                      </div>
                    </div>
                    <Box sx={{ my: 3 }}>
                      <CountdownTimer targetDate={dateTimeAfterThreeDays} />
                    </Box>
                    <PrimaryButton className="w-100" onClick={openNextSession}>
                      View session details
                    </PrimaryButton>
                  </>
                ) : (
                  <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                    No upcoming sessions
                  </Typography>
                )}
              </WhiteBgCard>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-lg-8">
              <div className="row">

                <div className="col-12 col-lg-5 mb-4">

                  <WhiteBgCard>
                    <Typography variant="body2" className="mb-4">
                      Posts engagements
                    </Typography>
                    <Box sx={[progressBgStyle, { backgroundColor: 'secondary.light' }]}>
                      <HeartIcon sx={{ color: 'secondary.dark', fontSize: 16, mr: 1 }} />
                      <Typography sx={{ color: 'secondary.dark' }} variant="body2">
                        {`${expertEngagement.likes} likes`}
                      </Typography>
                    </Box>

                    <Box sx={[progressBgStyle, { backgroundColor: 'primary.light', mb: 0 }]}>
                      <MessageIcon sx={{ color: 'primary.main', fontSize: 16, mr: 1 }} />
                      <Typography sx={{ color: 'primary.main' }} variant="body2">
                        {`${expertEngagement.comments} comments`}
                      </Typography>
                    </Box>

                    {/* <Box sx={[progressBgStyle, { backgroundColor: 'grey.A400', mb: 0 }]}>
                      <SendIcon sx={{ color: 'text.primary', fontSize: 16, mr: 1 }} />
                      <Typography sx={{ color: 'text.primary', lineHeight: 1 }} variant="body2">
                        0 shares
                      </Typography>
                    </Box> */}

                  </WhiteBgCard>

                </div>

                <div className="col-12 col-lg-7 mb-4">
                  <WhiteBgCard>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                      Completed Payouts
                    </Typography>

                    <div className="row gx-3 gx-xl-4">

                      <div className="col-6 col-sm-4 mb-3 mb-sm-0">
                        <Box sx={[greyBoxStyle]}>
                          <Typography variant="h6" sx={{ mb: 1, lineHeight: 1 }}>
                            {`${getCurrencySymbol(currencyType)} ${amountFormat(sessionEarnings)}`}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'grey.A700', lineHeight: 1.1 }}>
                            Sessions
                          </Typography>
                        </Box>
                      </div>

                      <div className="col-6 col-sm-4 mb-3 mb-sm-0">
                        <Box sx={[greyBoxStyle]}>
                          <Typography variant="h6" sx={{ mb: 1, lineHeight: 1 }}>
                            {`${getCurrencySymbol(currencyType)} ${amountFormat(profileEarnings)}`}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'grey.A700', lineHeight: 1.1 }}>
                            Profile
                          </Typography>
                        </Box>
                      </div>

                      <div className="col-6 col-sm-4">
                        <Box sx={[greyBoxStyle]}>
                          <Typography variant="h6" sx={{ mb: 1, lineHeight: 1 }}>
                            {`${getCurrencySymbol(currencyType)} ${amountFormat(sessionEarnings + profileEarnings)}`}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'grey.A700', lineHeight: 1.1 }}>
                            Total
                          </Typography>
                        </Box>
                      </div>

                    </div>
                  </WhiteBgCard>
                </div>

                <div className="col-12 col-lg-5 mb-4">
                  <WhiteBgCard>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                      Coaching hours
                    </Typography>

                    <Typography variant="h3" sx={{ lineHeight: 1, mb: 3 }}>
                      {secondsToHoursAndMinutes(totalDuration)}
                    </Typography>

                    <Box className="d-flex flex-nowrap" sx={{ mb: 4 }}>
                      {/* <Box sx={[coachingProgressStyle, {
                        backgroundColor: 'grey.900', width: '20%', borderTopLeftRadius: 6, borderBottomLeftRadius: 6,
                      }]}
                      /> */}
                      <Box sx={[coachingProgressStyle, {
                        backgroundColor: 'secondary.main', width: '50%', borderRadius: 0,
                      }]}
                      />
                      <Box sx={[coachingProgressStyle, {
                        backgroundColor: 'primary.main', width: '20%', borderTopRightRadius: 6, borderBottomRightRadius: 6,
                      }]}
                      />
                    </Box>

                    <div>

                      {/* <div className="d-flex align-items-center mb-2">
                        <Box
                          sx={{
                            mr: 1,
                            width: 7,
                            height: 7,
                            backgroundColor: 'grey.900',
                            borderRadius: 5,
                          }}
                        />
                        <Typography variant="body2" sx={{ color: 'grey.A700' }}>
                          {`Digital sessions : ${secondsToHoursAndMinutes(totalDigitalSessionDuration)}` }
                           </Typography>
                      </div> */}

                      <div className="d-flex align-items-center mb-2">
                        <Box
                          sx={{
                            mr: 1,
                            width: 7,
                            height: 7,
                            backgroundColor: 'secondary.main',
                            borderRadius: 5,
                          }}
                        />

                        <Typography variant="body2" sx={{ color: 'grey.A700' }}>
                          {`LIVE sessions :  ${secondsToHoursAndMinutes(totalLiveSessionDuration)}`}
                        </Typography>
                      </div>

                      <div className="d-flex align-items-center">
                        <Box
                          sx={{
                            mr: 1,
                            width: 7,
                            height: 7,
                            backgroundColor: 'primary.main',
                            borderRadius: 5,
                          }}
                        />
                        <Typography variant="body2" sx={{ color: 'grey.A700' }}>
                          {`LIVE classes : ${secondsToHoursAndMinutes(totalLiveClassDuration)}`}
                        </Typography>
                      </div>

                    </div>

                  </WhiteBgCard>
                </div>

                <div className="col-12 col-lg-7 mb-4">
                  <WhiteBgCard>
                    <Typography variant="body2" className="mb-4">
                      Sessions overview
                    </Typography>

                    <div className="row gx-3 gx-xl-4">

                      <div className="col-6 col-sm-4 mb-3 mb-sm-0">
                        <Box sx={[sessionBoxStyle, { backgroundColor: 'secondary.light' }]}>
                          <Typography variant="h3" sx={{ color: 'secondary.dark', mb: 1, lineHeight: 1 }}>
                            {sessionsOverview.bookedSessions}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'secondary.dark', lineHeight: 1.1 }}>
                            Booked sessions
                          </Typography>
                        </Box>
                      </div>

                      <div className="col-6 col-sm-4 mb-3 mb-sm-0">
                        <Box sx={[sessionBoxStyle, { backgroundColor: 'primary.light' }]}>
                          <Typography variant="h3" sx={{ color: 'primary.main', mb: 1, lineHeight: 1 }}>
                            {sessionsOverview.completedSessions}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'primary.main', lineHeight: 1.1 }}>
                            Sessions completed
                          </Typography>
                        </Box>
                      </div>

                      <div className="col-6 col-sm-4">
                        <Box sx={[sessionBoxStyle, { backgroundColor: 'grey.A400' }]}>
                          <Typography variant="h3" sx={{ color: 'grey.900', mb: 1, lineHeight: 1.1 }}>
                            {sessionsOverview.pendingSessions}
                          </Typography>
                          <Typography variant="body2" sx={{ lineHeight: 1 }}>
                            Pending sessions
                          </Typography>
                        </Box>
                      </div>

                    </div>
                  </WhiteBgCard>
                </div>

              </div>
            </div>
            <div className="col-12 col-lg-4">
              <WhiteBgCard>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  Top performing sessions
                </Typography>

                {topSessions.map((topSession, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={index}>
                    <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
                      {topSession.meta.title}
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      {truncateString(removeTags(topSession.meta.description), 100)}
                    </Typography>

                    <div className="d-flex align-items-center">
                      <EyeIcon sx={{ fontSize: 16, color: 'secondary.dark' }} />
                      <Typography variant="body2" sx={{ color: 'secondary.dark', ml: 0.75 }}>
                        {`${topSession.purchases} purchases`}
                      </Typography>
                      {/*
                      <BookmarkIcon sx={{ fontSize: 16, color: 'secondary.dark', ml: 2 }} />

                      <Typography variant="body2" sx={{ color: 'secondary.dark', ml: 0.75 }}>
                        255 views
                      </Typography> */}
                    </div>

                    {index !== topSessions.length - 1 && <Divider sx={{ my: 3 }} />}
                  </React.Fragment>
                ))}

              </WhiteBgCard>
            </div>
          </div>
        </Loader>
      </div>
    </ExpertHubLayout>
  );
};

export default DashboardView;
